/**
 * Los hechos del libro para el camino de cinco etapas (Fase 12, R-09).
 *
 * Todo sale de lo que el motor ya guarda: el último informe, la serie de versiones, la bitácora y,
 * desde la 1.6.3, el plan. Lo que un motor no da se queda en `undefined` y la etapa sale «sin datos»;
 * nunca se inventa un estado ni se pinta un error.
 */
import { useEffect, useState } from "react";
import { pasadas, planDelLibro, ultimoInforme } from "../api";
import type { Fichero, Informe, PlanLibro, Resumen, Serie } from "../api";
import { estadoCamino } from "../logica";
import type { HechosDelLibro } from "../logica";

/** La nota del informe del lector: la media de sus pilares (0–10), con un decimal. */
export function notaDelInforme(inf: Informe | null | undefined): number | null {
  const v = Object.values(inf?.data?.scores ?? {}).filter((x) => typeof x === "number");
  return v.length ? Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10 : null;
}

export function useCamino({ dir, doc, ficheros, resumen, pulso }: {
  dir: string; doc: string | null; ficheros: Fichero[]; resumen: Resumen | null; pulso: number;
}) {
  const [informe, setInforme] = useState<Informe | null | undefined>(undefined);
  const [serie, setSerie] = useState<Serie | null | undefined>(undefined);
  const [plan, setPlan] = useState<PlanLibro | null | "sin_ruta" | undefined>(undefined);

  // REINTENTO (Rais, 23-sep: «se ha vuelto a perder el informe y todo»). Al actualizar, el motor se
  // reinicia; si el Studio pregunta en ese instante, falla, y hasta hoy se quedaba con «sin datos»
  // hasta que el autor recargaba. Ahora, si algo falla, se vuelve a preguntar a los pocos segundos.
  const [intento, setIntento] = useState(0);
  useEffect(() => {
    if (!dir) { setInforme(null); setSerie(null); setPlan(null); return; }
    let vigente = true;
    let fallos = 0;
    const falla = () => { fallos++; };
    Promise.allSettled([
      ultimoInforme(dir, doc).then((i) => { if (vigente) setInforme(i); }).catch(() => { falla(); if (vigente) setInforme(undefined); }),
      pasadas(dir, doc).then((s) => { if (vigente) setSerie(s); }).catch(() => { falla(); if (vigente) setSerie(undefined); }),
      planDelLibro(dir, doc).then((p) => { if (vigente) setPlan(p); }).catch(() => { falla(); if (vigente) setPlan(undefined); }),
    ]).then(() => {
      if (vigente && fallos && intento < 6) window.setTimeout(() => { if (vigente) setIntento((n) => n + 1); }, 4000);
    });
    return () => { vigente = false; };
  }, [dir, doc, pulso, intento]);

  // Sin la ruta del plan (motor anterior a la 1.6.3), se mira si hay un plan en la carpeta.
  const planEnCarpeta = ficheros.some((f) => /plan-reescritura|^plan\./i.test(f.name));
  // Las saltadas no cuentan: el autor decidió no hacerlas (17-sep).
  const pasadasPlan = plan && plan !== "sin_ruta" ? (plan.areas ?? []).flatMap((a) => a.pasadas ?? []).filter((p) => p.estado !== "saltada") : [];
  const hechos: HechosDelLibro = {
    manuscrito: !!doc,
    informe: informe === undefined ? undefined
      : informe ? { nota: notaDelInforme(informe), fecha: null } : null,
    plan: plan === undefined ? undefined
      : plan === "sin_ruta" ? (planEnCarpeta ? { aprobado: true, total: 0, hechas: 0 } : null)
      : plan ? { aprobado: plan.aprobado, total: pasadasPlan.length, hechas: pasadasPlan.filter((p) => p.estado === "hecha").length }
      : null,
    versiones: serie === undefined ? undefined : serie?.versiones?.length ?? 0,
    correcciones: resumen ? resumen.porAgente?.copyeditor?.ops ?? 0 : undefined,
    publicacion: resumen ? resumen.porAgente?.publicar?.ops ?? 0 : undefined,
  };
  const versiones = serie?.versiones ?? [];
  const ultima = versiones.length ? versiones[versiones.length - 1] : null;
  // `planSoportado`: el motor tiene la ruta del plan (1.6.3). Sin ella, el plan se pide por el chat.
  return { hechos, etapas: estadoCamino(hechos), informe, serie, versiones, ultima, plan: plan === "sin_ruta" ? null : plan ?? null, planEnCarpeta, planSoportado: plan !== "sin_ruta" && plan !== undefined };
}
